import {
  usePatientTaskProgramsQuery,
  usePatientTasksListQuery,
} from '@common/cache';
import { useState } from 'react';
import { paths, TASK_PRIORITY } from '@common/types';
import { useLocation } from 'react-router-dom';
import { useDrawerStore } from 'store/drawer.store.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAllWorklistStore } from '@store';
import { ITaskTableEntry } from '../TaskTable/hooks';
import { NonOptionalArrayProps } from '@common/types/utilities';
import { SelectChangeEvent } from '@mui/material';
import { isEqual } from 'lodash';

export type IPatientTasksFilters = NonOptionalArrayProps<
  NonNullable<
    paths[`/patient/{patient_id}/tasks`]['get']['parameters']['query']
  >
>;

const initialFilters: IPatientTasksFilters = {
  category: '',
  sort_by: 'last_updated_at',
  sort_order: 'DESC',
  page: 1,
  limit: 100,
  programs: [''] as unknown as IPatientTasksFilters['programs'],
  priorities: [''] as unknown as IPatientTasksFilters['priorities'],
  due_date: '',
  statuses: [''] as unknown as IPatientTasksFilters['statuses'],
  // All of the below are unused, but are needed by the endpoint because it is very similar to the one in worklist
  location_building_ids: [],
  assignee_id: '',
  partner_id: '',
  practice_id: '',
  last_updated_after: '',
  task_names: [],
  order_categories: [],
  order_types: [],
  provider_ids: [],
};

const statusOptions = [
  {
    value: 'Completed',
    label: `Completed`,
  },
  {
    value: 'In progress',
    label: `In progress`,
  },
  {
    value: 'Pending',
    label: `Not Started`,
  },
];

const priorityOptions = [
  {
    value: 'Overdue',
    label: `Overdue`,
  },
  {
    value: 'Urgent',
    label: `Urgent`,
  },
  {
    value: 'High',
    label: `High`,
  },
  {
    value: 'Medium',
    label: `Medium`,
  },
  {
    value: 'Low',
    label: `Low`,
  },
];

interface IUseTasksProps {
  patient_id: string;
}

export const useTasks = ({ patient_id }: IUseTasksProps) => {
  const location = useLocation();
  const client = useQueryClient();

  const { data: programOptions = [], isPending: isProgramPending } =
    usePatientTaskProgramsQuery(patient_id);

  const { setDrawerOpen, setTaskId, taskId } = useDrawerStore(state => state);
  const { providerIds, partnerId } = useAllWorklistStore();

  const [filters, setFilters] = useState<IPatientTasksFilters>({
    ...initialFilters,
    search: location.state ? location.state : '',
    provider_ids: providerIds,
    partner_id: partnerId,
  });

  const isInitialFilters = isEqual(
    {
      ...initialFilters,
      search: location.state ? location.state : '',
      provider_ids: providerIds,
      partner_id: partnerId,
    },
    filters,
  );

  const handleCloseDrawer = () => {
    setTaskId('');
    setDrawerOpen(false);
  };

  const handleOpenTaskDetails = (id: string) => {
    setTaskId(id);
    setDrawerOpen(true);
    client.invalidateQueries({
      queryKey: ['task'],
    });
  };

  const handleFilterChange = (newFilters: Partial<IPatientTasksFilters>) => {
    setFilters({ ...filters, page: 1, ...newFilters });
  };

  const handleChangeFilterProgram = (event: SelectChangeEvent) => {
    const program = event.target.value;
    handleFilterChange({
      programs: [program] as IPatientTasksFilters['programs'],
    });
  };

  const handleChangeFilterPriority = (event: SelectChangeEvent) => {
    const priority = event.target.value;
    handleFilterChange({
      priorities: [priority] as IPatientTasksFilters['priorities'],
    });
  };

  const handleChangeFilterStatus = (event: SelectChangeEvent) => {
    const status = event.target.value;
    handleFilterChange({
      statuses: [status] as IPatientTasksFilters['statuses'],
    });
  };

  const handleResetFilter = () => {
    setFilters({
      ...initialFilters,
      search: location.state ? location.state : '',
      provider_ids: providerIds,
      partner_id: partnerId,
    });
  };

  const {
    data = {
      count: 0,
      tasks: [],
    },
    isFetching,
    isRefetching,
  } = usePatientTasksListQuery({ patient_id, ...filters });

  const entries: ITaskTableEntry[] = data.tasks.map(task => ({
    ...task,
    priority: task.priority as TASK_PRIORITY,
  }));

  const isResetDisabled = isEqual(filters, {
    ...initialFilters,
    search: location.state ? location.state : '',
    provider_ids: providerIds,
    partner_id: partnerId,
  });

  return {
    isInitialFilters,
    entries,
    filters,
    handleFilterChange,
    handleChangeFilterProgram,
    handleChangeFilterStatus,
    handleChangeFilterPriority,
    selectedTaskId: taskId,
    handleCloseDrawer,
    handleOpenTaskDetails,
    isLoading: isFetching || isRefetching || isProgramPending,
    totalCount: data.count,
    programOptions: programOptions.map(program => ({
      value: program.name,
      label: program.name,
    })),
    statusOptions,
    priorityOptions,
    handleResetFilter,
    isResetDisabled,
  };
};
